// Formato de impresión del ticket (POS → Configuración → Formato de impresión).
// Personaliza qué se muestra en el ticket/NC (encabezado, cuerpo, pie), el ancho
// de papel, el número de copias, el formato de la NC, y el DISEÑADOR DE LÍNEAS
// (bloques de texto/QR/separador/espacio) del encabezado y del pie. Vista previa en vivo.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlignCenter, AlignLeft, AlignRight, ArrowDown, ArrowUp, MoveVertical, Printer, QrCode, RotateCcw, Rows3, SeparatorHorizontal, Trash2, Type } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useAuth } from '@/lib/hooks';
import { Campo, Switch, fieldCls, posRpc } from './shared';
import {
  DEFAULTS, ticketHtml, type BloqueAlign, type BloqueTamano, type BloqueTicket,
  type EmpresaTicket, type ImpresionConfig, type TicketImprimible,
} from './print';
import { LOCALE } from '@/lib/utils/regional';

// Ticket de ejemplo para la vista previa en vivo: 3 líneas (una con descuento),
// pagos mixtos, clave de acceso, fidelidad — para que se vean todos los toggles.
const SAMPLE: TicketImprimible = {
  codigo: 'VTA-000123', tipoDoc: 'factura', serie: '001-101-000000123',
  claveAcceso: '2308202601179012345600110010010001231234567819',
  fecha: new Date().toLocaleString(LOCALE), caja: 'Caja 1', cajero: 'Ana Torres',
  cliente: 'Juan Pérez (0102030405)',
  lineas: [
    { descripcion: 'Arroz Superior 2kg', cantidad: 2, precio_unitario: 3.5, descuento: 0.2, subtotal: 6.6 },
    { descripcion: 'Aceite Girasol 1L', cantidad: 1, precio_unitario: 2.85, subtotal: 2.85 },
    { descripcion: 'Leche Entera 1L', cantidad: 3, precio_unitario: 1.1, subtotal: 3.3 },
  ],
  subtotal: 12.75, iva: 1.53, total: 14.28, recibido: 20, vuelto: 5.72,
  pagos: [{ forma: 'Efectivo', monto: 20 }],
  puntosGanados: 14, puntosSaldo: 320, cashbackGanado: 0.28, monederoSaldo: 4.15,
  sellos: [{ campana: 'Café x9', sellos_ganados: 1, premios: 0 }],
};

// --- Diseñador de líneas: alineación / tamaño / fuente de QR -------------
const ALINEACIONES: { value: BloqueAlign; label: string; Icon: typeof AlignLeft }[] = [
  { value: 'izq', label: 'Izquierda', Icon: AlignLeft },
  { value: 'centro', label: 'Centro', Icon: AlignCenter },
  { value: 'der', label: 'Derecha', Icon: AlignRight },
];
const TAMANOS: { value: BloqueTamano; label: string }[] = [
  { value: 'peq', label: 'Pequeño' }, { value: 'normal', label: 'Normal' }, { value: 'grande', label: 'Grande' },
];
const FUENTES_QR: { value: NonNullable<BloqueTicket['qr_fuente']>; label: string }[] = [
  { value: 'clave_acceso', label: 'Clave de acceso SRI' },
  { value: 'url', label: 'URL' },
  { value: 'texto', label: 'Texto libre' },
];
const TOKENS_AYUDA = '{empresa.nombre} {empresa.ruc} {empresa.direccion} {empresa.telefono} {fecha} {cliente} {cajero} {caja} {serie} {codigo} {total} {subtotal} {iva}';

const nuevoBloque = (tipo: BloqueTicket['tipo']): BloqueTicket => {
  switch (tipo) {
    case 'texto': return { tipo: 'texto', texto: '', align: 'centro', bold: false, tamano: 'normal' };
    case 'qr': return { tipo: 'qr', qr_fuente: 'url', qr_valor: '', align: 'centro', tamano: 'normal' };
    case 'separador': return { tipo: 'separador' };
    case 'espacio': return { tipo: 'espacio' };
  }
};

function AlineacionBotones({ value, onChange }: { value: BloqueAlign; onChange: (v: BloqueAlign) => void }) {
  return (
    <div className="flex gap-1">
      {ALINEACIONES.map(({ value: v, label, Icon }) => (
        <button key={v} type="button" onClick={() => onChange(v)} title={label}
          className={`rounded border px-2 py-1.5 ${v === value ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
  );
}

function BloqueItem({ b, onChange, onUp, onDown, onDelete, isFirst, isLast }: {
  b: BloqueTicket; onChange: (b: BloqueTicket) => void;
  onUp: () => void; onDown: () => void; onDelete: () => void; isFirst: boolean; isLast: boolean;
}) {
  const set = <K extends keyof BloqueTicket>(k: K, v: BloqueTicket[K]) => onChange({ ...b, [k]: v });
  const titulo = b.tipo === 'texto' ? 'Texto' : b.tipo === 'qr' ? 'Código QR' : b.tipo === 'separador' ? 'Línea separadora' : 'Espacio';

  return (
    <div className="space-y-2 rounded-md border bg-muted/20 p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase text-muted-foreground">{titulo}</span>
        <div className="flex items-center gap-1">
          <button type="button" disabled={isFirst} onClick={onUp} title="Subir"
            className="rounded p-1 hover:bg-muted disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
          <button type="button" disabled={isLast} onClick={onDown} title="Bajar"
            className="rounded p-1 hover:bg-muted disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={onDelete} title="Eliminar línea"
            className="rounded p-1 text-destructive hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>

      {b.tipo === 'texto' && (
        <div className="space-y-2">
          <textarea className={`${fieldCls} h-auto py-2`} rows={2} value={b.texto ?? ''}
            onChange={(e) => set('texto', e.target.value)}
            placeholder="Texto de la línea… admite tokens, p. ej. {empresa.nombre}" />
          <div className="flex flex-wrap items-center gap-3">
            <AlineacionBotones value={b.align ?? 'centro'} onChange={(v) => set('align', v)} />
            <Switch checked={!!b.bold} onChange={(v) => set('bold', v)} label="Negrita" />
            <select className={`${fieldCls} h-8 w-auto text-xs`} value={b.tamano ?? 'normal'}
              onChange={(e) => set('tamano', e.target.value as BloqueTamano)}>
              {TAMANOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <p className="text-[11px] text-muted-foreground">Tokens disponibles: {TOKENS_AYUDA}</p>
        </div>
      )}

      {b.tipo === 'qr' && (
        <div className="space-y-2">
          <Campo label="Contenido del QR">
            <select className={fieldCls} value={b.qr_fuente ?? 'url'}
              onChange={(e) => set('qr_fuente', e.target.value as BloqueTicket['qr_fuente'])}>
              {FUENTES_QR.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </Campo>
          {(b.qr_fuente === 'url' || b.qr_fuente === 'texto') && (
            <input className={fieldCls} value={b.qr_valor ?? ''} onChange={(e) => set('qr_valor', e.target.value)}
              placeholder={b.qr_fuente === 'url' ? 'https://…' : 'Texto a codificar (admite tokens)'} />
          )}
          {b.qr_fuente === 'clave_acceso' && (
            <p className="text-[11px] text-muted-foreground">
              Codifica la clave de acceso SRI del comprobante. Si el documento no la tiene (nota de venta), esta línea se omite al imprimir.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <AlineacionBotones value={b.align ?? 'centro'} onChange={(v) => set('align', v)} />
            <select className={`${fieldCls} h-8 w-auto text-xs`} value={b.tamano ?? 'normal'}
              onChange={(e) => set('tamano', e.target.value as BloqueTamano)}>
              {TAMANOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <input className={fieldCls} value={b.texto ?? ''} onChange={(e) => set('texto', e.target.value)}
            placeholder="Leyenda debajo del QR (opcional)" />
        </div>
      )}

      {b.tipo === 'separador' && <p className="text-xs text-muted-foreground">Línea punteada divisoria — sin más configuración.</p>}
      {b.tipo === 'espacio' && <p className="text-xs text-muted-foreground">Espacio en blanco — sin más configuración.</p>}
    </div>
  );
}

function BloquesEditor({ titulo, ayuda, bloques, onChange }: {
  titulo: string; ayuda: string; bloques: BloqueTicket[]; onChange: (b: BloqueTicket[]) => void;
}) {
  const agregar = (tipo: BloqueTicket['tipo']) => onChange([...bloques, nuevoBloque(tipo)]);
  const actualizar = (i: number, b: BloqueTicket) => onChange(bloques.map((x, idx) => (idx === i ? b : x)));
  const eliminar = (i: number) => onChange(bloques.filter((_, idx) => idx !== i));
  const mover = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= bloques.length) return;
    const copia = [...bloques];
    [copia[i], copia[j]] = [copia[j], copia[i]];
    onChange(copia);
  };

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Rows3 className="h-4 w-4" /> {titulo}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">{ayuda}</p>
        {bloques.length === 0 && <p className="text-xs italic text-muted-foreground">Sin líneas configuradas.</p>}
        <div className="space-y-2">
          {bloques.map((b, i) => (
            <BloqueItem key={i} b={b} onChange={(nb) => actualizar(i, nb)}
              onUp={() => mover(i, -1)} onDown={() => mover(i, 1)} onDelete={() => eliminar(i)}
              isFirst={i === 0} isLast={i === bloques.length - 1} />
          ))}
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={() => agregar('texto')}><Type className="mr-1 h-3.5 w-3.5" /> Texto</Button>
          <Button type="button" variant="outline" size="sm" onClick={() => agregar('qr')}><QrCode className="mr-1 h-3.5 w-3.5" /> Código QR</Button>
          <Button type="button" variant="outline" size="sm" onClick={() => agregar('separador')}><SeparatorHorizontal className="mr-1 h-3.5 w-3.5" /> Separador</Button>
          <Button type="button" variant="outline" size="sm" onClick={() => agregar('espacio')}><MoveVertical className="mr-1 h-3.5 w-3.5" /> Espacio</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function ConfigImpresion() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: cfg } = useQuery<ImpresionConfig>({
    queryKey: ['pos-impresion-config'],
    queryFn: () => posRpc<ImpresionConfig>('pos_impresion_config_get'),
    staleTime: 0,
  });
  const { data: empresa } = useQuery<EmpresaTicket>({
    queryKey: ['pos-emp-impresion'], enabled: !!user?.empresa?.id,
    queryFn: async () => (await apiClient.get<EmpresaTicket[]>(
      `/rest/autenticacion.empresas?id=eq.${user!.empresa.id}&select=nombre,ruc,direccion,telefono,logo,moneda:moneda_funcional`)).data[0] ?? {},
  });

  const [f, setF] = useState<ImpresionConfig>(DEFAULTS);
  useEffect(() => { if (cfg) setF(cfg); }, [cfg]);
  const set = <K extends keyof ImpresionConfig>(k: K, v: ImpresionConfig[K]) => setF((s) => ({ ...s, [k]: v }));

  // Vista previa en vivo: async por el QR (data-URI) — se recalcula al vuelo con cada cambio.
  const [preview, setPreview] = useState<string>('');
  useEffect(() => {
    let cancelado = false;
    void ticketHtml(SAMPLE, empresa ?? {}, f).then((html) => { if (!cancelado) setPreview(html); });
    return () => { cancelado = true; };
  }, [empresa, f]);

  const guardar = useMutation({
    mutationFn: () => posRpc<ImpresionConfig>('pos_impresion_config_set', { _cfg: f }),
    onSuccess: () => { toast.success('Formato de impresión guardado'); void qc.invalidateQueries({ queryKey: ['pos-impresion-config'] }); },
    onError: (e) => {
      const msg = getErrorMessage(e);
      if (/insufficient_privilege|permission denied/i.test(msg)) toast.error('Solo un administrador puede cambiar el formato de impresión');
      else toast.error(msg);
    },
  });

  const restaurar = () => { setF(DEFAULTS); toast.info('Valores por defecto cargados — recuerda Guardar'); };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Printer className="w-5 h-5" /> Formato de impresión</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={restaurar}><RotateCcw className="w-4 h-4 mr-1" /> Restaurar valores por defecto</Button>
          <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* --- Formulario --- */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Encabezado</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <Switch checked={f.mostrar_logo} onChange={(v) => set('mostrar_logo', v)} label="Logo" />
                <Switch checked={f.mostrar_ruc} onChange={(v) => set('mostrar_ruc', v)} label="RUC" />
                <Switch checked={f.mostrar_direccion} onChange={(v) => set('mostrar_direccion', v)} label="Dirección" />
                <Switch checked={f.mostrar_telefono} onChange={(v) => set('mostrar_telefono', v)} label="Teléfono" />
              </div>
              <Campo label="Texto extra bajo el encabezado (opcional)">
                <textarea className={fieldCls} rows={2} value={f.encabezado_extra ?? ''}
                  onChange={(e) => set('encabezado_extra', e.target.value || null)}
                  placeholder="p. ej. Horario de atención, redes sociales…" />
              </Campo>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Cuerpo</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Switch checked={f.mostrar_caja} onChange={(v) => set('mostrar_caja', v)} label="Caja" />
                <Switch checked={f.mostrar_cajero} onChange={(v) => set('mostrar_cajero', v)} label="Cajero" />
                <Switch checked={f.mostrar_cliente} onChange={(v) => set('mostrar_cliente', v)} label="Cliente" />
                <Switch checked={f.mostrar_clave_acceso} onChange={(v) => set('mostrar_clave_acceso', v)} label="Clave de acceso" />
                <Switch checked={f.mostrar_descuentos} onChange={(v) => set('mostrar_descuentos', v)} label="Descuentos" />
                <Switch checked={f.mostrar_fidelidad} onChange={(v) => set('mostrar_fidelidad', v)} label="Fidelidad (puntos/monedero/sellos)" />
                <Switch checked={f.mostrar_formas_pago} onChange={(v) => set('mostrar_formas_pago', v)} label="Formas de pago" />
                <Switch checked={f.mostrar_recibido_vuelto} onChange={(v) => set('mostrar_recibido_vuelto', v)} label="Recibido / vuelto" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Pie</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Campo label="Mensaje de despedida">
                <input className={fieldCls} value={f.pie_texto} onChange={(e) => set('pie_texto', e.target.value)} />
              </Campo>
              <Campo label="Leyenda legal (opcional, letra pequeña)">
                <textarea className={fieldCls} rows={2} value={f.pie_legal ?? ''}
                  onChange={(e) => set('pie_legal', e.target.value || null)}
                  placeholder="p. ej. políticas de cambio/devolución" />
              </Campo>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Papel</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Ancho de impresión (mm)">
                  <input type="number" min={40} max={80} step={1} className={fieldCls} value={f.ancho_mm}
                    onChange={(e) => set('ancho_mm', Math.min(80, Math.max(40, Number(e.target.value) || 80)))} />
                  <p className="mt-1 text-xs text-muted-foreground">Ancho útil del papel: 80 mm estándar, 58 mm angosto. Si se cortan columnas a la derecha, baja a ~72 (según tu impresora).</p>
                </Campo>
                <Campo label="Copias por venta">
                  <input type="number" min={1} max={5} className={fieldCls} value={f.copias}
                    onChange={(e) => set('copias', Math.min(5, Math.max(1, Number(e.target.value) || 1)))} />
                </Campo>
                <Campo label="Avance final (líneas)">
                  <input type="number" min={0} max={30} className={fieldCls} value={f.avance_final ?? 5}
                    onChange={(e) => set('avance_final', Math.min(30, Math.max(0, Number(e.target.value) || 0)))} />
                  <p className="mt-1 text-xs text-muted-foreground">Líneas en blanco al final para que el corte quede debajo de la última línea (súbelo si tu impresora corta muy arriba).</p>
                </Campo>
                <Campo label="Abrir cajón">
                  <select className={fieldCls} value={f.abrir_cajon ?? 'nunca'} onChange={(e) => set('abrir_cajon', e.target.value as 'nunca' | 'cada_venta' | 'solo_efectivo')}>
                    <option value="nunca">Nunca (o lo maneja el driver)</option>
                    <option value="solo_efectivo">Solo pagos en efectivo</option>
                    <option value="cada_venta">En cada venta</option>
                  </select>
                  <p className="mt-1 text-xs text-muted-foreground">Aplica en impresoras de RED (ESC/POS). En modo terminal el cajón lo dispara el driver de la impresora.</p>
                </Campo>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Nota de crédito</CardTitle></CardHeader>
            <CardContent>
              <Campo label="Formato de impresión">
                <select className={fieldCls} value={f.nc_formato} onChange={(e) => set('nc_formato', e.target.value as 'rollo80' | 'a4')}>
                  <option value="rollo80">Rollo (térmica)</option>
                  <option value="a4">A4 (ventana de impresión)</option>
                </select>
              </Campo>
              <p className="text-xs text-muted-foreground mt-1">
                Aplica al reembolso de devoluciones. La vista previa de la derecha muestra el TICKET de venta;
                la NC respeta los mismos toggles de encabezado/pie cuando se imprime en rollo.
              </p>
            </CardContent>
          </Card>

          <BloquesEditor titulo="Diseño de líneas — Encabezado" bloques={f.encabezado_bloques}
            ayuda="Se imprimen al final del encabezado (después del texto extra), antes del cuerpo del ticket."
            onChange={(b) => set('encabezado_bloques', b)} />

          <BloquesEditor titulo="Diseño de líneas — Pie" bloques={f.pie_bloques}
            ayuda="Se imprimen al final del pie (después del mensaje de despedida y la leyenda legal)."
            onChange={(b) => set('pie_bloques', b)} />
        </div>

        {/* --- Vista previa en vivo --- */}
        <div className="lg:sticky lg:top-4 self-start">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Vista previa</CardTitle></CardHeader>
            <CardContent>
              <iframe title="preview" srcDoc={preview} className="w-full h-[70vh] bg-white border rounded" />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
